import style from './attachPolicies.module.sass'
import React, { useEffect, useState, useCallback } from 'react'
import { Radio, AutoComplete, Spin, Icon, Divider } from 'antd'
import { ReactComponent as Delete } from '../../assets/svg/delete.svg'
import { ReactComponent as TeamIcon } from '../../assets/svg/teamPolicyIcon.svg'
import { ReactComponent as UserIcon } from '../../assets/svg/userPolicyIcon.svg'
import { ReactComponent as PlusIcon } from "../../assets/svg/plus_btn.svg";
import { useToast } from '../../helpers/commonFunction'
import { Http } from '../../helpers/axois'

const antIcon = <Icon type="isLoading" style={{ fontSize: 24 }} spin />

export const AttachPolicies = (props: any) => {

    const { ErrorToast } = useToast()

    const [mode, setMode] = useState('Team')

    const [arnList, setArnList] = useState<any[]>([])
    const [teamsList, setTeamsList] = useState<any[]>([])
    const [usersList, setUsersList] = useState<any[]>([])
    const [policyList, setPolicyList] = useState<any[]>([])

    //sources are used as options for autocomplete component
    const [teamsSource, setTeamsSource] = useState<any[]>([])
    const [usersSource, setUsersSource] = useState<any[]>([])
    const [policySource, setPolicySource] = useState<any[]>([])

    const [isLoading, setLoading] = useState(false)

    //here entity stands for either user or team
    const [entity, setEntity] = useState('')
    const [policy, setPolicy] = useState('')

    const { appId, orgId, themeId, envId, canAttach, canRemove } = props.data

    const fetchArn = useCallback(async () => {
        const arnPostBody = {
            appId: appId ? appId : null,
            themeId: themeId ? themeId : null,
            envId: envId ? envId : null
        }
        try {
            const res: any = await Http.post(`/arnPolicy?orgId=${orgId}`, arnPostBody).toPromise()
            setArnList(res)
        } catch (err) {
            ErrorToast('Failed to fetch policy mappings')
        }
    }, [appId, themeId, envId, ErrorToast, orgId])

    const fetchTeams = useCallback(async () => {
        let arnName: string;
        if (themeId) {
            arnName = `themes:${themeId}`
        } else if (envId) {
            arnName = `env:${envId}`
        } else {
            arnName = `apps:${appId}`
        }

        try {
            const res: any = await Http.get(`/teams/list/${orgId}?arn=${arnName}`).toPromise()
            setTeamsSource(res.map((item: any) => item.teamName))
            setTeamsList(res)
        } catch (err) {
            ErrorToast('Failed to fetch teams')
        }
    }, [orgId, themeId, envId, appId, ErrorToast])

    const fetchUsers = useCallback(async () => {
        try {
            const res: any = await Http.get(`/orgs/${orgId}/users`).toPromise()
            setUsersSource(res.map((item: any) => item.user_name))
            setUsersList(res)
        } catch (err) {
            ErrorToast('Failed to fetch users')
        }
    }, [orgId, ErrorToast])

    const fetchPolicies = useCallback(async () => {
        let arnName: string;
        if (themeId) {
            arnName = `themes:${themeId}`
        } else if (envId) {
            arnName = `env:${envId}`
        } else {
            arnName = `apps:${appId}`
        }
        try {
            const res: any = await Http.get(`/policy?orgId=${orgId}&arn=${arnName}`).toPromise()
            setPolicySource(res.map((item: any) => item.name))
            setPolicyList(res)
        } catch (err) {
            ErrorToast('Failed to fetch policies')
        }
    }, [orgId, themeId, envId, appId, ErrorToast])

    const addPolicy = async () => {

        let entityId: any;
        if (mode === 'Team') {
            const entityData = teamsList.find((item: any) => item.teamName === entity)
            entityId = entityData && entityData.id
        } else {
            const entityData = usersList.find((item: any) => item.user_name === entity)
            entityId = entityData && entityData.usr_id
        }

        const policyData = policyList.find((item: any) => item.name === policy)
        const policyId = policyData ? policyData.id_ : null

        //check if same record is requested for addition
        const isPresent = arnList.find((item: any) => {
            return (item.entity_id === entityId && item.policies_id === policyId)
        })
        if (isPresent) {
            ErrorToast('The entry you are trying to add is already present')
            return
        }

        //check emptyness
        if (entity === '') {
            ErrorToast(`${mode} cannot be empty`)
            return
        }

        if (entityId) {
            let arnName: string

            if (appId) {
                arnName = `apps:${appId}`
            } else if (themeId) {
                arnName = `themes:${themeId}`
            } else {
                arnName = `env:${envId}`
            }

            const reqBody = policyId ?
                {
                    arn: arnName,
                    entityType: mode.toLowerCase(),
                    entityId,
                    policyId,
                    orgId
                } :
                {
                    arn: arnName,
                    entityType: mode.toLowerCase(),
                    entityId,
                    orgId
                }
            setLoading(true)
            try {
                await Http.post(`/addArnPolicy`, reqBody).toPromise()
                await fetchArn()
                setEntity('')
                setPolicy('')
                setLoading(false)
            } catch (err) {
                ErrorToast(`Failed to add policy mappings`)
                setLoading(false)
            }
        } else {
            ErrorToast('Invalid entry. Please check')
        }
    }

    const removePolicy = async (id: any) => {
        setLoading(true)
        let arnName: string;
        if (themeId) {
            arnName = `themes:${themeId}`
        } else if (envId) {
            arnName = `env:${envId}`
        } else {
            arnName = `apps:${appId}`
        }
        try {
            await Http.delete(`/removeArnPolicy/${id}?orgId=${orgId}&arn=${arnName}`).toPromise()
            await fetchArn()
            setEntity('')
            setPolicy('')
            setLoading(false)
        } catch (err) {
            ErrorToast('Error removing policy mapping')
            setLoading(false)
        }
    }

    useEffect(() => {
        setLoading(true);
        (async () => {
            try {
                await fetchArn()
                await fetchTeams()
                await fetchUsers()
                await fetchPolicies()
                setLoading(false)
            } catch (e) {
                ErrorToast('Error while fetching initial data')
            }
        })()
    }, [fetchArn, fetchTeams, fetchUsers, fetchPolicies, ErrorToast])

    return (
        !isLoading ?
            <div className={style.attachContainer}>
                <div style={{ marginTop: 0 }} className={style.label}>Type</div>
                <Radio.Group data-test-id="type" onChange={(e: any) => {
                    setMode(e.target.value)
                    setEntity('')
                }} value={mode}>
                    <Radio value='Team'>Team</Radio>
                    <Radio value='User'>User</Radio>
                </Radio.Group>
                <div className={style.rowContainer}>
                    <div style={{ marginRight: '16px', flexGrow: 1, minWidth: '210px' }} >
                        <AutoComplete
                            data-test-id="auto_complete"
                            style={{ width: '100%' }}
                            placeholder={`Select ${mode}`}
                            dataSource={mode === 'Team' ? teamsSource : usersSource}
                            filterOption={true}
                            onSelect={(value: any) => setEntity(value)}
                            onChange={(value: any) => setEntity(value)}
                            value={entity}
                        />
                    </div>
                    <AutoComplete
                        data-test-id="select_policy"
                        style={{ marginRight: '16px', flexGrow: 1, minWidth: '210px' }}
                        placeholder={`Select Policy`}
                        dataSource={policySource}
                        filterOption={true}
                        onSelect={(value: any) => setPolicy(value)}
                        onChange={(value: any) => setPolicy(value)}
                        value={policy}
                    />
                    {
                        canAttach && <PlusIcon data-test-id="add_policy" onClick={addPolicy} style={{ margin: '0', height: '28px', width: '36px' }} />
                    }
                </div>
                <Divider />
                {/* <div className={style.label}>List</div> */}
                <div className={style.policyListContainer}>

                    {
                        arnList.map((item: any, index: any) => {
                            let name: string;
                            if (item.entity_type === 'team') {
                                name = teamsList.find((team: any) => team.id === item.entity_id).teamName
                            } else {
                                name = usersList.find((user: any) => user.usr_id === item.entity_id).user_name
                            }
                            const policyData = policyList.find((el: any) => el.id_ === item.policies_id)
                            const policyName = policyData && policyData.name
                            return (
                                <div key={index} className={style.rowContainer}>
                                    <div style={{ marginRight: '16px', flexGrow: 1, minWidth: '210px', position: 'relative' }} >
                                        <AutoComplete
                                            style={{ width: '100%' }}
                                            value={name}
                                        />
                                        <div style={{ position: 'absolute', top: item.entity_type === 'team' ? '-4px' : '-2px', right: 0 }}>
                                            {
                                                item.entity_type === 'team' ?
                                                    <TeamIcon data-test-id="team_icon" height='24px' width='24px'></TeamIcon>
                                                    :
                                                    <UserIcon data-test-id="user_icon" height='18px' width='18px'></UserIcon>
                                            }
                                        </div>
                                    </div>
                                    <AutoComplete
                                        data-test-id="policy_autocomplete"
                                        style={{ marginRight: '16px', flexGrow: 1, minWidth: '210px' }}
                                        value={policyName}
                                    />
                                    {
                                        canRemove && <Delete data-test-id="policy_delete" onClick={() => removePolicy(item.id_)} className={style.deleteIcon} height="20px" width="20px" />
                                    }
                                </div>
                            )
                        })
                    }

                </div>
            </div>
            :
            <div className={style.spinnerContainer}>
                <Spin indicator={antIcon} style={{ color: '#15808C' }} />
            </div>
    )
}
